import ApiService from '@/services/api.service';
import {
  downloadStyledSpreadsheet,
  sheetBlank,
  sheetData,
  sheetHeader,
  sheetHeading,
  sheetMeta,
  sheetSection,
} from '@/utils/styledSpreadsheetExport';

export function escapeCSVField(field) {
  if (field === null || field === undefined) return '';
  const stringField = String(field);
  if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('"')) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  return stringField;
}

export function formatCsvAmount(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return value.toFixed(2);
}

export function formatDisplayDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export function isDirectExpense(record) {
  return record?.expenseKind === 'Cash Expense' || record?.expenseKind === 'Credit Card Expense';
}

export function getRecordTypeLabel(record) {
  if (!record) return '-';
  if (record.type === 'Bill') return 'Bill';
  if (record.expenseKind === 'Cash Expense') return 'Cash Expense';
  if (record.expenseKind === 'Credit Card Expense') return 'Credit Card Expense';
  return 'Payment';
}

export function manualAccountRecords(accountRecords) {
  if (!Array.isArray(accountRecords)) return [];
  return accountRecords.filter((row) => row.isManualRecord === true);
}

export function formatAccountName(account) {
  if (!account) return 'Account';
  if (account.account) {
    const accountNumber = account.account.accountNumber || '';
    const accountName = account.account.accountName || '';
    return accountNumber ? `${accountNumber} - ${accountName}` : accountName;
  }
  return account.accountName || account.description || 'Account';
}

export function formatPayFromAccount(account) {
  if (!account) return '-';
  const accountNumber = account.accountNumber || '';
  const accountName = account.accountName || '';
  return accountNumber ? `${accountNumber} - ${accountName}` : accountName || '-';
}

export function formatRecordStatus(record) {
  if (!record) return '-';
  if (record.status) return record.status;
  if (record.type === 'Expense') return 'Paid';
  const balance = Number(record.balance || 0);
  const paid = Number(record.paidAmount || 0);
  if (balance <= 0 && paid > 0) return 'Paid';
  if (paid > 0) return 'Partial';
  return 'Open';
}

export function getLineTotalAmount(line) {
  return Number(line?.total ?? line?.amount ?? 0);
}

export function getLineBalanceAmount(line) {
  if (line?.balanceAmount != null && line.balanceAmount !== '') {
    return Math.max(0, Number(line.balanceAmount) || 0);
  }
  const total = getLineTotalAmount(line);
  const paid = Number(line?.paidAmount || 0);
  return Math.max(0, Math.round((total - paid) * 100) / 100);
}

export function getLineRecordStatusLabel(line) {
  if (line?.status) return line.status;
  const total = getLineTotalAmount(line);
  const paid = Number(line?.paidAmount || 0);
  const balance = getLineBalanceAmount(line);
  if (total > 0 && (paid >= total || balance <= 0)) return 'Bill Paid';
  if (paid > 0) return 'Partially Paid';
  return 'Not Paid';
}

export async function fetchBillLineDetailsById(billId) {
  const [accountRecords, itemRecords, consultantRecords] = await Promise.all([
    ApiService.get(`/vendor/enter-bill/${billId}/accounts`).then((res) => res.data).catch(() => []),
    ApiService.get(`/vendor/enter-bill/${billId}/items`).then((res) => res.data).catch(() => []),
    ApiService.get(`/vendor/enter-bill/${billId}/consultants`).then((res) => res.data).catch(() => [])
  ]);

  return {
    accountRecords: Array.isArray(accountRecords) ? accountRecords : [],
    itemRecords: Array.isArray(itemRecords) ? itemRecords : [],
    consultantRecords: Array.isArray(consultantRecords) ? consultantRecords : []
  };
}

export async function fetchBillDetails(record) {
  record.details = await fetchBillLineDetailsById(record.id);
}

async function fetchDirectExpenseDetails(record) {
  const endpoint = record.expenseKind === 'Cash Expense'
    ? '/vendor/cash-expense'
    : '/vendor/credit-card-expense';
  const allExpenses = await ApiService.get(endpoint).then((res) => res.data).catch(() => []);
  const expense = (Array.isArray(allExpenses) ? allExpenses : [])
    .find((row) => String(row.id) === String(record.id));

  record.details = {
    accountRecords: expense?.accountRecords || [],
    itemRecords: expense?.itemRecords || [],
    comments: expense?.comments || record.comments || ''
  };
}

async function fetchPayBillExpenseDetails(record) {
  const payBillRecords = await ApiService.get(`/vendor/pay-bill/${record.id}/records`)
    .then((res) => res.data)
    .catch(() => []);

  const groupedByBill = {};

  if (Array.isArray(payBillRecords)) {
    payBillRecords.forEach((payRecord) => {
      const billId = payRecord.billId || payRecord.bill?.id;
      if (!billId) return;

      if (!groupedByBill[billId]) {
        groupedByBill[billId] = {
          ...payRecord,
          showDetails: false,
          loadingDetails: false,
          paymentRecords: [payRecord]
        };
      } else {
        groupedByBill[billId].paymentRecords.push(payRecord);
      }
    });

    Object.keys(groupedByBill).forEach((billId) => {
      const grouped = groupedByBill[billId];
      let billPaidAmount = Number(grouped.bill?.paidAmount || 0);
      if (billPaidAmount === 0 && grouped.paymentRecords?.length) {
        billPaidAmount = grouped.paymentRecords.reduce(
          (sum, pr) => sum + Number(pr.amountPaid || 0),
          0
        );
      }
      grouped.amountPaid = billPaidAmount;
    });
  }

  record.details = {
    payBillRecords: Object.values(groupedByBill)
  };
}

export async function fetchExpenseDetails(record) {
  if (isDirectExpense(record)) {
    await fetchDirectExpenseDetails(record);
    return;
  }
  await fetchPayBillExpenseDetails(record);
}

export function hasBillDetailRecords(details) {
  if (!details) return false;
  return (
    manualAccountRecords(details.accountRecords).length > 0
    || (details.itemRecords && details.itemRecords.length > 0)
    || (details.consultantRecords && details.consultantRecords.length > 0)
  );
}

export async function ensurePayRecordBillDetails(payRecord) {
  const billId = payRecord.bill?.id || payRecord.billId;
  if (!billId) return;

  const hasLineDetails = payRecord.bill && hasBillDetailRecords(payRecord.bill);
  if (hasLineDetails) return;

  const lineDetails = await fetchBillLineDetailsById(billId);
  payRecord.bill = {
    ...(payRecord.bill || {}),
    ...lineDetails
  };
}

export async function ensureRecordDetails(record) {
  if (!record.details) {
    if (record.type === 'Bill') {
      await fetchBillDetails(record);
    } else if (record.type === 'Expense') {
      await fetchExpenseDetails(record);
    }
  }

  if (record.type !== 'Expense' || isDirectExpense(record) || !record.details?.payBillRecords) {
    return;
  }

  await Promise.all(record.details.payBillRecords.map(async (payRecord) => {
    await ensurePayRecordBillDetails(payRecord);
  }));
}

export function getBillDisplayLines(details = {}) {
  const lines = [];

  manualAccountRecords(details.accountRecords || []).forEach((account) => {
    lines.push({
      lineType: 'Account',
      name: formatAccountName(account),
      description: account.description || '-',
      quantity: '',
      unitPrice: '',
      hours: '',
      hourlyRate: '',
      amount: account.amount,
      paidAmount: account.paidAmount,
      balanceAmount: getLineBalanceAmount(account),
      status: getLineRecordStatusLabel(account)
    });
  });

  (details.itemRecords || []).forEach((item) => {
    lines.push({
      lineType: 'Item',
      name: item.description || 'Item',
      description: item.description || '-',
      quantity: item.quantity ?? '',
      unitPrice: item.unitPrice,
      hours: '',
      hourlyRate: '',
      amount: item.total,
      paidAmount: item.paidAmount,
      balanceAmount: getLineBalanceAmount(item),
      status: getLineRecordStatusLabel(item)
    });
  });

  (details.consultantRecords || []).forEach((consultant) => {
    lines.push({
      lineType: 'Consultant',
      name: consultant.consultantName || '-',
      description: consultant.description || '-',
      quantity: '',
      unitPrice: '',
      hours: consultant.hours ?? '',
      hourlyRate: consultant.hourlyRate,
      amount: consultant.total,
      paidAmount: consultant.paidAmount,
      balanceAmount: getLineBalanceAmount(consultant),
      status: getLineRecordStatusLabel(consultant)
    });
  });

  return lines;
}

export function getRecordDisplayLines(record) {
  if (!record?.details) return [];

  if (record.type === 'Bill' || isDirectExpense(record)) {
    return getBillDisplayLines(record.details);
  }

  const lines = [];
  (record.details.payBillRecords || []).forEach((payRecord) => {
    lines.push({
      lineType: 'Payment',
      name: payRecord.bill?.billNo || 'Bill payment',
      description: `Payment for bill ${payRecord.bill?.billNo || ''}`,
      quantity: '',
      unitPrice: '',
      hours: '',
      hourlyRate: '',
      amount: payRecord.amountPaid,
      paidAmount: payRecord.amountPaid,
      balanceAmount: 0,
      status: payRecord.bill?.status || 'Paid',
      paymentMethod: payRecord.paymentMethod || '',
      paymentAccount: formatPayFromAccount(payRecord.payFromAccount)
    });

    if (payRecord.bill) {
      getBillDisplayLines(payRecord.bill).forEach((line) => {
        lines.push({
          ...line,
          relatedBillNo: payRecord.bill?.billNo || '',
          paymentMethod: payRecord.paymentMethod || '',
          paymentAccount: formatPayFromAccount(payRecord.payFromAccount)
        });
      });
    }
  });

  return lines;
}

export function getCsvHeaders() {
  return [
    'Record Type',
    'Record Date',
    'Record No',
    'Vendor Name',
    'Record Amount',
    'Record Balance',
    'Record Status',
    'Voided',
    'Due Date',
    'Due Days',
    'Overdue Days',
    'Line Type',
    'Line #',
    'Account / Consultant / Item',
    'Line Description',
    'Quantity',
    'Unit Price',
    'Hours',
    'Hourly Rate',
    'Line Amount',
    'Paid Amount',
    'Line Balance',
    'Line Status',
    'Related Bill No',
    'Related Bill Date',
    'Payment Amount',
    'Payment Method',
    'Payment Account',
    'Comments'
  ];
}

const RECORD_SUMMARY_HEADERS = [
  'Type',
  'Date',
  'Record No',
  'Vendor Name',
  'Original Amount',
  'Balance',
  'Status',
  'Voided',
  'Due Date',
  'Due Days',
  'Overdue Days',
  'Comments',
];

function buildRecordSummarySection(record, trackerMeta = {}) {
  return [
    sheetSection('Record Summary'),
    sheetHeader(...RECORD_SUMMARY_HEADERS),
    sheetData(
      getRecordTypeLabel(record),
      formatDisplayDate(record.date),
      record.no || '',
      record.vendorName || '',
      formatCsvAmount(record.originalAmount),
      record.type === 'Expense' ? '' : formatCsvAmount(record.balance),
      formatRecordStatus(record),
      record.voided ? 'Yes' : 'No',
      record.type === 'Bill' ? formatDisplayDate(record.dueDate) : '',
      record.type === 'Bill' ? (trackerMeta.dueDays ?? '') : '',
      record.type === 'Bill' ? (trackerMeta.overdueDays ?? '') : '',
      record.comments || ''
    ),
  ];
}

function buildConsultantTableRows(consultantRecords = []) {
  if (!consultantRecords.length) return [];
  const rows = [
    sheetBlank(),
    sheetSection('Consultant Records'),
    sheetHeader(
      'Consultant Name',
      'Description',
      'Hours',
      'Hourly Rate',
      'Total',
      'Paid Amount',
      'Balance Amount',
      'Status'
    ),
  ];
  consultantRecords.forEach((consultant) => {
    rows.push(sheetData(
      consultant.consultantName || '',
      consultant.description || '',
      consultant.hours ?? '',
      formatCsvAmount(consultant.hourlyRate),
      formatCsvAmount(consultant.total),
      formatCsvAmount(consultant.paidAmount),
      formatCsvAmount(getLineBalanceAmount(consultant)),
      getLineRecordStatusLabel(consultant)
    ));
  });
  return rows;
}

function buildAccountTableRows(accountRecords = [], { manualOnly = true } = {}) {
  const accounts = manualOnly ? manualAccountRecords(accountRecords) : (accountRecords || []);
  if (!accounts.length) return [];
  const rows = [
    sheetBlank(),
    sheetSection('Account Records'),
    sheetHeader('Account', 'Description', 'Amount', 'Paid Amount', 'Balance Amount', 'Status'),
  ];
  accounts.forEach((account) => {
    rows.push(sheetData(
      formatAccountName(account),
      account.description || '',
      formatCsvAmount(account.amount),
      formatCsvAmount(account.paidAmount),
      formatCsvAmount(getLineBalanceAmount(account)),
      getLineRecordStatusLabel(account)
    ));
  });
  return rows;
}

function buildItemTableRows(itemRecords = [], { includePaymentCols = true } = {}) {
  if (!itemRecords?.length) return [];
  const rows = [
    sheetBlank(),
    sheetSection('Item Records'),
  ];
  if (includePaymentCols) {
    rows.push(sheetHeader(
      'Description',
      'Quantity',
      'Unit Price',
      'Total',
      'Paid Amount',
      'Balance Amount',
      'Status'
    ));
    itemRecords.forEach((item) => {
      rows.push(sheetData(
        item.description || '',
        item.quantity ?? '',
        formatCsvAmount(item.unitPrice),
        formatCsvAmount(item.total),
        formatCsvAmount(item.paidAmount),
        formatCsvAmount(getLineBalanceAmount(item)),
        getLineRecordStatusLabel(item)
      ));
    });
  } else {
    rows.push(sheetHeader('Description', 'Quantity', 'Unit Price', 'Total'));
    itemRecords.forEach((item) => {
      const total = item.total ?? ((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0));
      rows.push(sheetData(
        item.description || '',
        item.quantity ?? '',
        formatCsvAmount(item.unitPrice),
        formatCsvAmount(total)
      ));
    });
  }
  return rows;
}

function buildBillDetailSections(details = {}) {
  return [
    ...buildAccountTableRows(details.accountRecords || []),
    ...buildItemTableRows(details.itemRecords || []),
    ...buildConsultantTableRows(details.consultantRecords || []),
  ];
}

function buildPaidBillsSection(record) {
  const payBillRecords = record.details?.payBillRecords || [];
  if (!payBillRecords.length) {
    return [
      sheetBlank(),
      sheetSection('Paid Bills'),
      sheetData('No paid bill details available'),
    ];
  }

  const rows = [
    sheetBlank(),
    sheetSection('Paid Bills'),
    sheetHeader(
      'Bill No',
      'Bill Date',
      'Bill Total',
      'Amount Paid',
      'Payment Method',
      'Payment Account',
      'Bill Status'
    ),
  ];

  payBillRecords.forEach((payRecord) => {
    rows.push(sheetData(
      payRecord.bill?.billNo || '',
      formatDisplayDate(payRecord.bill?.date),
      formatCsvAmount(payRecord.bill?.total),
      formatCsvAmount(payRecord.amountPaid),
      payRecord.paymentMethod || '',
      formatPayFromAccount(payRecord.payFromAccount),
      payRecord.bill?.status || 'Paid'
    ));

    if (payRecord.bill) {
      rows.push(sheetBlank());
      rows.push(sheetSection(`Bill lines: ${payRecord.bill.billNo || payRecord.bill?.id || ''}`));
      rows.push(...buildBillDetailSections(payRecord.bill));
    }
  });

  return rows;
}

function buildDirectExpenseSections(record) {
  const details = record.details || {};
  const rows = [
    ...buildAccountTableRows(details.accountRecords || [], { manualOnly: false }),
    ...buildItemTableRows(details.itemRecords || [], { includePaymentCols: false }),
  ];

  if (details.comments || record.comments) {
    rows.push(sheetBlank());
    rows.push(sheetMeta('Comments', details.comments || record.comments || ''));
  }

  if (!rows.length) {
    rows.push(
      sheetBlank(),
      sheetSection('Expense Lines'),
      sheetData('No detailed lines available'),
      sheetMeta('Amount', formatCsvAmount(record.originalAmount))
    );
  }

  return rows;
}

export function buildSectionalCsvForRecord(record, trackerMeta = {}, index = 1, total = 1) {
  const rows = [
    sheetBlank(),
    sheetSection(`Record ${index} of ${total}`),
    ...buildRecordSummarySection(record, trackerMeta),
  ];

  if (!record.details) {
    rows.push(
      sheetBlank(),
      sheetSection('Details'),
      sheetData('No detailed lines available')
    );
    return rows;
  }

  if (record.type === 'Bill') {
    const detailRows = buildBillDetailSections(record.details);
    if (!detailRows.length) {
      rows.push(sheetBlank(), sheetSection('Details'), sheetData('No detailed lines available'));
    } else {
      rows.push(...detailRows);
    }
    return rows;
  }

  if (isDirectExpense(record)) {
    rows.push(...buildDirectExpenseSections(record));
    return rows;
  }

  rows.push(...buildPaidBillsSection(record));
  return rows;
}

/** @deprecated Flat row layout — use buildSectionalCsvForRecord */
function buildCsvLineRow(record, lineMeta = {}, trackerMeta = {}) {
  return [
    escapeCSVField(getRecordTypeLabel(record)),
    escapeCSVField(formatDisplayDate(record.date)),
    escapeCSVField(record.no || ''),
    escapeCSVField(record.vendorName || ''),
    escapeCSVField(formatCsvAmount(record.originalAmount)),
    escapeCSVField(record.type === 'Expense' ? '' : formatCsvAmount(record.balance)),
    escapeCSVField(formatRecordStatus(record)),
    escapeCSVField(record.voided ? 'Yes' : 'No'),
    escapeCSVField(formatDisplayDate(record.dueDate)),
    escapeCSVField(trackerMeta.dueDays ?? ''),
    escapeCSVField(trackerMeta.overdueDays ?? ''),
    escapeCSVField(lineMeta.lineCategory || ''),
    escapeCSVField(lineMeta.lineNumber ?? ''),
    escapeCSVField(lineMeta.accountOrName || ''),
    escapeCSVField(lineMeta.description || ''),
    escapeCSVField(lineMeta.quantity ?? ''),
    escapeCSVField(lineMeta.unitPrice ?? ''),
    escapeCSVField(lineMeta.hours ?? ''),
    escapeCSVField(lineMeta.hourlyRate ?? ''),
    escapeCSVField(lineMeta.lineAmount ?? ''),
    escapeCSVField(lineMeta.paidAmount ?? ''),
    escapeCSVField(lineMeta.lineBalance ?? ''),
    escapeCSVField(lineMeta.lineStatus || ''),
    escapeCSVField(lineMeta.relatedBillNo || ''),
    escapeCSVField(lineMeta.relatedBillDate || ''),
    escapeCSVField(lineMeta.paymentAmount ?? ''),
    escapeCSVField(lineMeta.paymentMethod || ''),
    escapeCSVField(lineMeta.paymentAccount || ''),
    escapeCSVField(lineMeta.comments || '')
  ].join(',');
}

function buildBillLineCsvRows(record, details, context = {}, startLineNumber = 1, trackerMeta = {}) {
  const rows = [];
  let lineNumber = startLineNumber;

  const pushLine = (lineMeta) => {
    rows.push(buildCsvLineRow(record, {
      lineNumber,
      ...context,
      ...lineMeta
    }, trackerMeta));
    lineNumber += 1;
  };

  manualAccountRecords(details.accountRecords || []).forEach((account) => {
    pushLine({
      lineCategory: 'Account',
      accountOrName: formatAccountName(account),
      description: account.description || '',
      lineAmount: formatCsvAmount(account.amount),
      paidAmount: formatCsvAmount(account.paidAmount),
      lineBalance: formatCsvAmount(getLineBalanceAmount(account)),
      lineStatus: getLineRecordStatusLabel(account)
    });
  });

  (details.itemRecords || []).forEach((item) => {
    pushLine({
      lineCategory: 'Item',
      accountOrName: item.description || 'Item',
      description: item.description || '',
      quantity: item.quantity ?? '',
      unitPrice: formatCsvAmount(item.unitPrice),
      lineAmount: formatCsvAmount(item.total),
      paidAmount: formatCsvAmount(item.paidAmount),
      lineBalance: formatCsvAmount(getLineBalanceAmount(item)),
      lineStatus: getLineRecordStatusLabel(item)
    });
  });

  (details.consultantRecords || []).forEach((consultant) => {
    pushLine({
      lineCategory: 'Consultant',
      accountOrName: consultant.consultantName || '',
      description: consultant.description || '',
      hours: consultant.hours ?? '',
      hourlyRate: formatCsvAmount(consultant.hourlyRate),
      lineAmount: formatCsvAmount(consultant.total),
      paidAmount: formatCsvAmount(consultant.paidAmount),
      lineBalance: formatCsvAmount(getLineBalanceAmount(consultant)),
      lineStatus: getLineRecordStatusLabel(consultant)
    });
  });

  return rows;
}

function buildDirectExpenseCsvRows(record, trackerMeta = {}) {
  const rows = [];
  const details = record.details || {};
  let lineNumber = 1;
  const comments = details.comments || record.comments || '';

  const pushLine = (lineMeta) => {
    rows.push(buildCsvLineRow(record, {
      lineNumber,
      comments: lineNumber === 1 ? comments : '',
      ...lineMeta
    }, trackerMeta));
    lineNumber += 1;
  };

  (details.accountRecords || []).forEach((account) => {
    pushLine({
      lineCategory: 'Account',
      accountOrName: formatAccountName(account) || account.accountId || '',
      description: account.description || '',
      lineAmount: formatCsvAmount(account.amount)
    });
  });

  (details.itemRecords || []).forEach((item) => {
    const total = item.total ?? ((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0));
    pushLine({
      lineCategory: 'Item',
      accountOrName: item.description || 'Item',
      description: item.description || '',
      quantity: item.quantity ?? '',
      unitPrice: formatCsvAmount(item.unitPrice),
      lineAmount: formatCsvAmount(total)
    });
  });

  if (!rows.length) {
    rows.push(buildCsvLineRow(record, {
      lineNumber: 1,
      lineCategory: 'Summary',
      description: 'No detailed lines available',
      lineAmount: formatCsvAmount(record.originalAmount)
    }, trackerMeta));
  }

  return rows;
}

export function buildDetailedCsvRowsForRecord(record, trackerMeta = {}) {
  if (!record.details) {
    return [buildCsvLineRow(record, {
      lineNumber: 1,
      lineCategory: 'Summary',
      description: 'No detailed lines available',
      lineAmount: formatCsvAmount(record.originalAmount)
    }, trackerMeta)];
  }

  if (record.type === 'Bill') {
    const lineRows = buildBillLineCsvRows(record, record.details, {}, 1, trackerMeta);
    return lineRows.length ? lineRows : [buildCsvLineRow(record, {
      lineNumber: 1,
      lineCategory: 'Summary',
      description: 'No detailed lines available',
      lineAmount: formatCsvAmount(record.originalAmount)
    }, trackerMeta)];
  }

  if (isDirectExpense(record)) {
    return buildDirectExpenseCsvRows(record, trackerMeta);
  }

  const rows = [];
  let lineNumber = 1;
  const payBillRecords = record.details.payBillRecords || [];

  payBillRecords.forEach((payRecord) => {
    const paymentContext = {
      relatedBillNo: payRecord.bill?.billNo || '',
      relatedBillDate: formatDisplayDate(payRecord.bill?.date),
      paymentAmount: formatCsvAmount(payRecord.amountPaid),
      paymentMethod: payRecord.paymentMethod || '',
      paymentAccount: formatPayFromAccount(payRecord.payFromAccount)
    };

    rows.push(buildCsvLineRow(record, {
      lineNumber,
      lineCategory: 'Payment',
      accountOrName: payRecord.bill?.billNo || 'Bill payment',
      description: `Payment for bill ${payRecord.bill?.billNo || ''}`,
      lineAmount: paymentContext.paymentAmount,
      lineStatus: payRecord.bill?.status || 'Paid',
      ...paymentContext
    }, trackerMeta));
    lineNumber += 1;

    if (payRecord.bill) {
      const billLines = buildBillLineCsvRows(record, payRecord.bill, paymentContext, lineNumber, trackerMeta);
      rows.push(...billLines);
      lineNumber += billLines.length;
    }
  });

  return rows.length ? rows : [buildCsvLineRow(record, {
    lineNumber: 1,
    lineCategory: 'Summary',
    description: 'No detailed lines available',
    lineAmount: formatCsvAmount(record.originalAmount)
  }, trackerMeta)];
}

export async function exportDetailedBillsCsv(records, options = {}) {
  const {
    filename = 'bills_and_expenses_detailed.xls',
    getTrackerMeta = () => ({}),
    onProgress
  } = options;

  if (!records?.length) {
    return { success: false, lineCount: 0, recordCount: 0 };
  }

  for (let index = 0; index < records.length; index += 1) {
    const record = records[index];
    await ensureRecordDetails(record);
    if (onProgress) onProgress(index + 1, records.length);
  }

  const exportedAt = new Date().toLocaleString('en-US');
  const sheetRows = [
    sheetHeading('Bills and Expenses Export'),
    sheetMeta('Exported', exportedAt),
    sheetMeta('Total Records', records.length),
    sheetBlank(),
    ...records.flatMap((record, index) =>
      buildSectionalCsvForRecord(record, getTrackerMeta(record), index + 1, records.length)
    ),
  ];

  downloadStyledSpreadsheet(sheetRows, filename);

  return {
    success: true,
    lineCount: sheetRows.length,
    recordCount: records.length
  };
}
